import { ReloadOutlined } from '@ant-design/icons';
import type { RecommendDecision, RecommendRow } from '@hq/api-sdk';
import { ApiError } from '@hq/api-sdk';
import { formatAmount, formatDisplayTime, formatPercent } from '@hq/utils';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  App,
  Button,
  Card,
  Descriptions,
  Input,
  Modal,
  Radio,
  Segmented,
  Space,
  Table,
  Tag,
  Typography
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useState } from 'react';
import { hq } from '../session';

/** 推荐状态（`wl_ai_recommend.status`）：NEW/VIEWED 可确认，采纳后仍需走调价申请。 */
const STATUS_META: Record<string, { text: string; color: string }> = {
  NEW: { text: '待确认', color: 'processing' },
  VIEWED: { text: '已查看', color: 'blue' },
  ADOPTED: { text: '已采纳', color: 'green' },
  REJECTED: { text: '已驳回', color: 'red' }
};

const STATUS_FILTERS: { value: string; label: string }[] = [
  { value: 'ALL', label: '全部' },
  { value: 'NEW', label: '待确认' },
  { value: 'ADOPTED', label: '已采纳' },
  { value: 'REJECTED', label: '已驳回' }
];

interface ConfirmState {
  row: RecommendRow;
  action: RecommendDecision;
  opinion: string;
}

/**
 * 替代 / 降本推荐（docs/05 §4.2）：
 * 推荐由 AI 中台按历史用量与协议价生成，采购人必须二次确认；
 * 采纳只改推荐状态，不自动改价（改价仍走「调价申请」）。
 */
export default function Substitute() {
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const [filter, setFilter] = useState('ALL');
  const [confirmState, setConfirmState] = useState<ConfirmState | null>(null);
  const [evidence, setEvidence] = useState<RecommendRow | null>(null);

  const list = useQuery({
    queryKey: ['ops', 'substitute', filter],
    queryFn: () => hq.aiInsights.recommend(filter === 'ALL' ? undefined : filter)
  });

  const invalidate = async () => {
    await queryClient.invalidateQueries({ queryKey: ['ops', 'substitute'] });
  };

  const run = useMutation({
    mutationFn: () => hq.aiInsights.runRecommend(),
    onSuccess: async () => {
      message.success('推荐已重算');
      await invalidate();
    },
    onError: (error) =>
      message.error(error instanceof ApiError ? error.friendlyMessage : '重算推荐失败')
  });

  const confirm = useMutation({
    mutationFn: (state: ConfirmState) =>
      hq.aiInsights.confirmRecommend(state.row.id, {
        action: state.action,
        opinion: state.opinion.trim() || undefined
      }),
    onSuccess: async (_data, state) => {
      message.success(state.action === 'ADOPT' ? '已采纳（如需改价请提交调价申请）' : '已驳回');
      setConfirmState(null);
      await invalidate();
    },
    onError: (error) =>
      message.error(error instanceof ApiError ? error.friendlyMessage : '确认推荐失败')
  });

  const columns: ColumnsType<RecommendRow> = [
    {
      title: '原物资',
      dataIndex: 'materialName',
      width: 180,
      render: (value: string | null, row) => value || row.materialId
    },
    {
      title: '推荐替代',
      dataIndex: 'recommendName',
      width: 180,
      render: (value: string | null, row) => (
        <Space>
          {value || row.recommendMaterialId}
          {row.recommendType ? <Tag>{row.recommendType}</Tag> : null}
        </Space>
      )
    },
    {
      title: '现价 / 目标价',
      width: 160,
      render: (_value, row) => `${formatAmount(row.currentPrice)} / ${formatAmount(row.targetPrice)}`
    },
    {
      title: '降本幅度',
      dataIndex: 'saveRatio',
      width: 110,
      align: 'right',
      render: (value: number) => formatPercent(value)
    },
    {
      title: '年用量',
      dataIndex: 'annualQty',
      width: 100,
      align: 'right'
    },
    {
      title: '预计年节省',
      dataIndex: 'annualSaveAmount',
      width: 140,
      align: 'right',
      render: (value: number) => <Typography.Text type="success">{formatAmount(value)}</Typography.Text>
    },
    {
      title: '生成时间',
      dataIndex: 'generateAt',
      width: 160,
      render: (value: string) => formatDisplayTime(value)
    },
    {
      title: '状态',
      dataIndex: 'status',
      width: 100,
      render: (value: string | null) => {
        const meta = STATUS_META[value ?? ''] ?? { text: value ?? '-', color: 'default' };
        return <Tag color={meta.color}>{meta.text}</Tag>;
      }
    },
    {
      title: '操作',
      width: 190,
      fixed: 'right',
      render: (_value, row) => {
        const pending = row.status === 'NEW' || row.status === 'VIEWED';
        return (
          <Space>
            <Button size="small" onClick={() => setEvidence(row)}>
              依据
            </Button>
            {pending ? (
              <>
                <Button
                  size="small"
                  type="primary"
                  onClick={() => setConfirmState({ row, action: 'ADOPT', opinion: '' })}
                >
                  采纳
                </Button>
                <Button
                  size="small"
                  danger
                  onClick={() => setConfirmState({ row, action: 'REJECT', opinion: '' })}
                >
                  驳回
                </Button>
              </>
            ) : null}
          </Space>
        );
      }
    }
  ];

  const error = list.error instanceof ApiError ? list.error.friendlyMessage : null;

  return (
    <Card
      className="page-card"
      title="替代 / 降本推荐"
      extra={
        <Space>
          <Segmented
            value={filter}
            options={STATUS_FILTERS}
            onChange={(value) => setFilter(String(value))}
          />
          <Button icon={<ReloadOutlined />} loading={list.isFetching} onClick={() => void list.refetch()}>
            刷新
          </Button>
          <Button type="primary" loading={run.isPending} onClick={() => run.mutate()}>
            重算推荐
          </Button>
        </Space>
      }
    >
      {error ? <Alert type="error" showIcon message="推荐加载失败" description={error} style={{ marginBottom: 16 }} /> : null}
      <Typography.Paragraph type="secondary">
        推荐由历史用量与协议价生成，采纳前请核对「依据」；采纳不自动改价，改价仍需提交调价申请。
      </Typography.Paragraph>
      <Table<RecommendRow>
        rowKey="id"
        size="small"
        columns={columns}
        dataSource={list.data ?? []}
        loading={list.isPending}
        pagination={{ pageSize: 20, showSizeChanger: false }}
        scroll={{ x: 1300 }}
      />

      <Modal
        title={`${confirmState?.action === 'ADOPT' ? '采纳' : '驳回'}推荐`}
        open={Boolean(confirmState)}
        confirmLoading={confirm.isPending}
        onCancel={() => setConfirmState(null)}
        onOk={() => {
          if (!confirmState) return;
          if (confirmState.action === 'REJECT' && !confirmState.opinion.trim()) {
            message.warning('驳回请填写原因，便于供应商举证追溯');
            return;
          }
          confirm.mutate(confirmState);
        }}
      >
        {confirmState ? (
          <Descriptions column={1} size="small" bordered>
            <Descriptions.Item label="原物资">
              {confirmState.row.materialName || confirmState.row.materialId}
            </Descriptions.Item>
            <Descriptions.Item label="替代物资">
              {confirmState.row.recommendName || confirmState.row.recommendMaterialId}
            </Descriptions.Item>
            <Descriptions.Item label="预计年节省">
              {formatAmount(confirmState.row.annualSaveAmount)}
            </Descriptions.Item>
          </Descriptions>
        ) : null}
        <Radio.Group
          style={{ margin: '16px 0' }}
          value={confirmState?.action}
          onChange={(event) =>
            setConfirmState((prev) => (prev ? { ...prev, action: event.target.value } : prev))
          }
        >
          <Radio.Button value="ADOPT">采纳</Radio.Button>
          <Radio.Button value="REJECT">驳回</Radio.Button>
        </Radio.Group>
        <Input.TextArea
          rows={3}
          maxLength={200}
          showCount
          placeholder="确认意见（驳回必填）"
          value={confirmState?.opinion ?? ''}
          onChange={(event) =>
            setConfirmState((prev) => (prev ? { ...prev, opinion: event.target.value } : prev))
          }
        />
      </Modal>

      <Modal
        title="推荐依据"
        open={Boolean(evidence)}
        footer={null}
        onCancel={() => setEvidence(null)}
        width={720}
      >
        <Typography.Paragraph type="secondary">
          依据由 AI 中台生成（DR-13.9），含来源单据、指标版本与生成时间。
        </Typography.Paragraph>
        <pre style={{ maxHeight: 420, overflow: 'auto', background: '#fafafa', padding: 12, borderRadius: 6 }}>
          {evidence?.evidenceJson
            ? (() => {
                try {
                  return JSON.stringify(JSON.parse(evidence.evidenceJson), null, 2);
                } catch {
                  return evidence.evidenceJson;
                }
              })()
            : '暂无依据数据'}
        </pre>
      </Modal>
    </Card>
  );
}
